import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ref, set, get, update, push } from 'firebase/database';
import { realtimeDb } from '../../firebase';
import { FaArrowLeft, FaPrint, FaWhatsapp, FaFilePdf, FaSave } from 'react-icons/fa';
import { formatDate } from '../../Utils/dateUtils';
import { normalizeCompanyKey, firebaseCompanyKey } from '../../Utils/companyUtils';

// Use global variables from CDN
const jsPDF = window.jspdf.jsPDF;
const html2canvas = window.html2canvas;

const GatePassPrintView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef();

  const { entries = [], company, division } = location.state || {};
  const [data, setData] = useState([]);
  const [finalChallanNo, setFinalChallanNo] = useState('');
  const [dispatchDate, setDispatchDate] = useState('');
  const [finalVehicleNo, setFinalVehicleNo] = useState('');
  const [address, setAddress] = useState('');
  const [logo, setLogo] = useState('');
  const [loading, setLoading] = useState(true);
  const [printAllowed, setPrintAllowed] = useState(false);
  const [printReady, setPrintReady] = useState(false);

  // Add print-specific styles to the document head
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @media print {
        body * {
          visibility: hidden;
        }
        .print-container, .print-container * {
          visibility: visible;
        }
        .print-container {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          margin: 0;
          padding: 0;
        }
        .no-print {
          display: none !important;
        }
        .print-only {
          display: block !important;
        }
        .print-page {
          page-break-after: always;
          page-break-inside: avoid;
          margin: 0;
          padding: 15mm;
          font-family: 'Arial', sans-serif;
          font-size: 14pt;
          line-height: 1.5;
        }
        .print-page:last-child {
          page-break-after: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 14pt;
        }
        th, td {
          border: 1px solid #000;
          padding: 8px;
          text-align: center;
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .header {
          font-size: 18pt;
          margin-bottom: 20px;
        }
        .gate-pass-title {
          font-size: 18pt;
          margin: 15px 0;
          text-decoration: underline;
        }
        .info-grid {
          font-size: 14pt;
          margin-bottom: 20px;
        }
        .signature-area {
          margin-top: 50px;
          font-size: 14pt;
        }
        img {
          max-width: 40mm;
          max-height: 40mm;
          object-fit: contain;
        }
        hr {
          border-top: 2px solid black;
          margin: 16px 0;
        }
        .company-details {
          line-height: 1.3;
          padding-left: 8px;
        }
      }
      .print-only {
        display: none;
      }
      hr {
        border-top: 2px solid black;
        margin: 16px 0;
      }
      .company-details {
        line-height: 1.3;
        padding-left: 8px;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  useEffect(() => {
    if (!company || !division || !entries.length) {
      alert("Missing data. Please return to register.");
      navigate(-1);
      return;
    }

    const fetchAndPrepare = async () => {
      try {
        const normalizedCompany = normalizeCompanyKey(company);
        const sanitizedCompany = firebaseCompanyKey(normalizedCompany);
        const sanitizedDivision = division;
        
        const entryList = entries.map((e, i) => ({
          ...e,
          id: e.id || e.entryId || i + 1,
          usedOil: e.usedOil || null,
          oilCap: e.oilCap || '',
          type: e.type || '',
          jobNo: e.jobNo || '',
          kva: e.kva || '',
          make: e.make || '',
          makeSrNo: e.makeSrNo || '',
          kg: e.kg || '',
        }));
        setData(entryList);

        const needsChallan = entryList.some(e => !e.finalChallanNo);
        if (needsChallan) {
          const metaRef = ref(realtimeDb, `companies/${sanitizedCompany}/metadata`);
          const metaSnap = await get(metaRef);
          let last = metaSnap.exists() ? metaSnap.val()?.lastChallanNo || 0 : 0;
          last += 1;
          const newChallan = `CHLN-${last}`;
          setFinalChallanNo(newChallan);
          await set(metaRef, { ...metaSnap.val(), lastChallanNo: last });
        } else {
          setFinalChallanNo(entryList[0]?.finalChallanNo || '');
        }

        setDispatchDate(entryList[0]?.dispatchDate || '');
        setFinalVehicleNo((entryList[0]?.finalVehicleNo || '').toUpperCase());

        const addrRef = ref(realtimeDb, `companyDetails/${sanitizedCompany}`);
        const addrSnap = await get(addrRef);
        if (addrSnap.exists()) {
          const data = addrSnap.val();
          setAddress(data.address || 'Address not available');
          setLogo(data.logo || '');
        } else {
          setAddress('Address not available');
          setLogo('');
        }

        setLoading(false);
        setPrintReady(true);
      } catch (error) {
        console.error("Error fetching data:", error);
        alert("Failed to load data. Please try again.");
        navigate(-1);
      }
    };

    fetchAndPrepare();
  }, [company, division, entries, navigate]);

  useEffect(() => {
    setPrintAllowed(dispatchDate && finalVehicleNo && printReady);
  }, [dispatchDate, finalVehicleNo, printReady]);

  const handleChange = (field, value) => {
    if (field === 'dispatchDate') setDispatchDate(value);
    if (field === 'finalVehicleNo') setFinalVehicleNo(value.toUpperCase());
    if (field === 'finalChallanNo') setFinalChallanNo(value.toUpperCase());
  };

  const handleRowChange = (id, field, value) => {
    setData(prev =>
      prev.map(row => row.id === id ? {
        ...row,
        [field]: value
      } : row)
    );
  };

  const handleBeforePrint = async () => {
    if (!printReady || !printRef.current) {
      alert('Print content not ready. Please try again.');
      return;
    }

    const normalizedCompany = normalizeCompanyKey(company);
    const sanitizedCompany = firebaseCompanyKey(normalizedCompany);
    const sanitizedDivision = division;
    const primaryPath = `companies/${sanitizedCompany}/${sanitizedDivision}/register`;
    const gatePassPath = `companies/${sanitizedCompany}/${sanitizedDivision}/gatePassSummary`;
    const updates = {};
    const jobDetails = {};

    data.forEach(row => {
      const rowPath = `${primaryPath}/${row.id}`;
      updates[`${rowPath}/jobNo`] = row.jobNo || '';
      updates[`${rowPath}/kva`] = row.kva || '';
      updates[`${rowPath}/make`] = row.make || '';
      updates[`${rowPath}/makeSrNo`] = row.makeSrNo || '';
      updates[`${rowPath}/type`] = row.type || '';
      updates[`${rowPath}/oilCap`] = row.oilCap || '';
      updates[`${rowPath}/kg`] = row.kg || '';
      if (row.usedOil !== null && row.usedOil !== undefined) {
        updates[`${rowPath}/usedOil`] = row.usedOil;
      }
      updates[`${rowPath}/finalChallanNo`] = finalChallanNo || row.finalChallanNo || '';
      updates[`${rowPath}/dispatchDate`] = dispatchDate;
      updates[`${rowPath}/finalVehicleNo`] = finalVehicleNo.toUpperCase();
      updates[`${rowPath}/completed`] = true;
      jobDetails[row.id] = {
        jobNo: row.jobNo || '',
        kva: row.kva || '',
        make: row.make || '',
        makeSrNo: row.makeSrNo || '',
        type: row.type || '',
        oilCap: row.oilCap || '',
        kg: row.kg || '',
        usedOil: row.usedOil !== null && row.usedOil !== undefined ? row.usedOil : (row.oilCap || '')
      };
    });

    try {
      // Update register entries
      await update(ref(realtimeDb), updates);

      // Save gate pass summary
      const gatePassRef = push(ref(realtimeDb, gatePassPath));
      const gatePassData = {
        gatePassNumber: finalChallanNo,
        gatePassDate: dispatchDate,
        finalVehicleNo: finalVehicleNo.toUpperCase(),
        jobDetails: jobDetails,
        totalOilUsed: (data.reduce((sum, row) => sum + (parseFloat(row.usedOil || row.oilCap) || 0), 0) * 1.03).toFixed(2),
        timestamp: Date.now(),
        status: 'Completed'
      };
      await set(gatePassRef, gatePassData);

      // Wait for state updates and DOM changes to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Trigger print
      window.print();
    } catch (error) {
      console.error("Error updating data:", error);
      alert(`Failed to update register or gate pass summary: ${error.message}`);
    }
  };

  const handleWhatsAppSharePDF = async () => {
    if (!data.length || !printReady || !printRef.current) {
      alert('No data available to share as PDF.');
      return;
    }

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const elements = printRef.current.querySelectorAll('.print-page');
      for (let i = 0; i < elements.length; i++) {
        const canvas = await html2canvas(elements[i], {
          scale: 2,
          useCORS: true,
          logging: false,
          allowTaint: true,
          width: elements[i].offsetWidth,
          height: elements[i].offsetHeight
        });
        
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth() - 20; // Add margins
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth, pdfHeight);
      }

      const pdfBlob = pdf.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `GatePass_${company}_${division}_${finalChallanNo || 'NoChallan'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Share via WhatsApp after a small delay
      setTimeout(() => {
        const message = `Gate Pass - ${company} (${division} Division)\nChallan No: ${finalChallanNo}\nDate: ${formatDate(dispatchDate)}\nVehicle No: ${finalVehicleNo}`;
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
      }, 1000);

      URL.revokeObjectURL(pdfUrl);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const baseTotalOil = data.reduce((sum, row) => sum + (parseFloat(row.usedOil || row.oilCap) || 0), 0);
  const totalOilUsed = baseTotalOil * 1.03;
  const totalKg = data.reduce((sum, row) => sum + (parseFloat(row.kg) || 0), 0);

  const renderGatePass = (copyTitle) => (
    <div className="print-page bg-white" 
         style={{
           width: '210mm',
           minHeight: '297mm',
           border: '2px solid black',
           boxSizing: 'border-box',
           margin: '0 auto',
           fontFamily: 'Arial, sans-serif',
           padding: '15mm',
           fontSize: '14pt',
           lineHeight: 1.5
         }}>
      <div className="flex items-center justify-start gap-4 mb-8">
        {logo && (
          <img
            src={logo}
            alt={`${company || 'Company'} Logo`}
            style={{ width: '40mm', height: '40mm', objectFit: 'contain' }}
          />
        )}
        <div className="text-left company-details">
          <h1 className="text-3xl font-bold uppercase mb-2" style={{ fontSize: '18pt' }}>{company || 'Company Name'}</h1>
          <div className="text-xl font-bold mb-1" style={{ fontSize: '16pt' }}>Transformer Service</div>
          <div className="text-xl font-bold mb-2" style={{ fontSize: '16pt' }}>{division || 'Division'} Gate Pass</div>
          <div className="text-lg font-semibold" style={{ fontSize: '12pt' }}>{address}</div>
        </div>
      </div>
      <hr className="border-t-2 border-black my-4" />
      <div className="text-center mb-6 font-bold underline" style={{ fontSize: '18pt' }}>
        GATE PASS
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8" style={{ fontSize: '14pt' }}>
        <div><strong>Date:</strong> {formatDate(dispatchDate) || 'N/A'}</div>
        <div><strong>Vehicle No:</strong> {finalVehicleNo || 'N/A'}</div>
        <div><strong>Challan No:</strong> {finalChallanNo || 'N/A'}</div>
        <div><strong>Total Oil:</strong> {totalOilUsed.toFixed(2)} L</div>
        <div><strong>Total Weight:</strong> {totalKg.toFixed(2)} kg</div>
      </div>

      <table className="w-full mb-12" style={{ fontSize: '14pt' }}>
        <thead>
          <tr className="bg-gray-200">
            <th className="p-2 border border-black">SR No</th>
            <th className="p-2 border border-black">Job No</th>
            <th className="p-2 border border-black">KVA</th>
            <th className="p-2 border border-black">Make</th>
            <th className="p-2 border border-black">Make SR No</th>
            <th className="p-2 border border-black">Type</th>
            <th className="p-2 border border-black">Oil Cap (L)</th>
            <th className="p-2 border border-black" style={{ backgroundColor: '#fef3c7' }}>KG (Weight)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, idx) => (
            <tr key={row.id}>
              <td className="p-2 border border-black text-center">{idx + 1}</td>
              <td className="p-2 border border-black text-center">
                <input
                  type="text"
                  value={row.jobNo || ''}
                  onChange={(e) => handleRowChange(row.id, 'jobNo', e.target.value)}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ fontSize: '14pt' }}
                />
                <span className="print-only">{row.jobNo || 'N/A'}</span>
              </td>
              <td className="p-2 border border-black text-center">
                <input
                  type="text"
                  value={row.kva || ''}
                  onChange={(e) => handleRowChange(row.id, 'kva', e.target.value)}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ fontSize: '14pt' }}
                />
                <span className="print-only">{row.kva || 'N/A'}</span>
              </td>
              <td className="p-2 border border-black text-center">
                <input
                  type="text"
                  value={row.make || ''}
                  onChange={(e) => handleRowChange(row.id, 'make', e.target.value)}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ fontSize: '14pt' }}
                />
                <span className="print-only">{row.make || 'N/A'}</span>
              </td>
              <td className="p-2 border border-black text-center">
                <input
                  type="text"
                  value={row.makeSrNo || ''}
                  onChange={(e) => handleRowChange(row.id, 'makeSrNo', e.target.value)}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ fontSize: '14pt' }}
                />
                <span className="print-only">{row.makeSrNo || 'N/A'}</span>
              </td>
              <td className="p-2 border border-black text-center">
                <input
                  type="text"
                  value={row.type || ''}
                  onChange={(e) => handleRowChange(row.id, 'type', e.target.value)}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ fontSize: '14pt' }}
                />
                <span className="print-only">{row.type || 'N/A'}</span>
              </td>
              <td className="p-2 border border-black text-center">
                <input
                  type="text"
                  value={row.usedOil !== null && row.usedOil !== undefined ? row.usedOil : (row.oilCap || '')}
                  onChange={(e) => {
                    handleRowChange(row.id, 'oilCap', e.target.value);
                    handleRowChange(row.id, 'usedOil', null); // clear usedOil so it falls back to oilCap
                  }}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ fontSize: '14pt' }}
                />
                <span className="print-only">{row.usedOil !== null && row.usedOil !== undefined ? row.usedOil : (row.oilCap || '0.00')}</span>
              </td>
              {/* KG Weight column */}
              <td className="p-2 border border-black text-center" style={{ backgroundColor: '#fffbeb' }}>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={row.kg || ''}
                  onChange={(e) => handleRowChange(row.id, 'kg', e.target.value)}
                  className="w-full text-center border-none bg-transparent focus:ring-0 no-print"
                  style={{ fontSize: '14pt' }}
                  placeholder="0.00"
                />
                <span className="print-only">{row.kg || '0.00'}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex justify-between mt-16" style={{ fontSize: '14pt' }}>
        <div className="text-center">
          <div className="border-t-2 border-black w-48 inline-block mt-12"></div>
          <div>Sign of Receiver</div>
        </div>
        <div className="text-center">
          <div className="border-t-2 border-black w-48 inline-block mt-12"></div>
          <div>Sign of {company || 'Company'}</div>
        </div>
      </div>
    </div>
  );

  if (loading) return <div className="p-4 text-center">Loading...</div>;

  return (
    <div className="bg-gray-100 min-h-screen p-4">
      {/* Header */}
      <div className="bg-white shadow-md rounded-lg p-4 mb-4 no-print">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate(-1)}
              className="text-blue-600 hover:text-blue-800"
            >
              <FaArrowLeft className="text-xl" />
            </button>
            <div>
              <h1 className="text-xl font-bold">{company} - {division} Division</h1>
              <h2 className="text-lg">Gate Pass Generation</h2>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            <div className="flex gap-2 items-center">
              <label className="font-medium">Challan No:</label>
              <input
                type="text"
                value={finalChallanNo}
                onChange={(e) => handleChange('finalChallanNo', e.target.value)}
                className="border rounded px-2 py-1 w-32 uppercase"
                required
              />
            </div>
            <div className="flex gap-2 items-center">
              <label className="font-medium">Date:</label>
              <input
                type="date"
                value={dispatchDate}
                onChange={(e) => handleChange('dispatchDate', e.target.value)}
                className="border rounded px-2 py-1 w-36"
                required
              />
            </div>
            <div className="flex gap-2 items-center">
              <label className="font-medium">Vehicle No:</label>
              <input
                type="text"
                value={finalVehicleNo}
                onChange={(e) => handleChange('finalVehicleNo', e.target.value)}
                className="border rounded px-2 py-1 w-32 uppercase"
                required
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 mb-4 no-print">
        <button
          onClick={handleWhatsAppSharePDF}
          disabled={!printAllowed}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg ${printAllowed ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
        >
          <FaFilePdf /> Share PDF
        </button>
        <button
          onClick={handleBeforePrint}
          disabled={!printAllowed}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg ${printAllowed ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
        >
          <FaPrint /> Submit & Print
        </button>
      </div>

      {/* Print Content - Each copy will be on a separate page */}
      <div ref={printRef} className="print-container">
        {renderGatePass('ORIGINAL')}
        {renderGatePass('DUPLICATE')}
      </div>
    </div>
  );
};

export default GatePassPrintView;
